import {
  AlertTriangle,
  Ban,
  Check,
  Copy,
  KeyRound,
  Plus,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { Page, PageHeader } from '../components/layout/AppShell';
import { Modal, useToast } from '../components/ui/overlays';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Input,
  LoadingBlock,
  Mono,
  Select,
  Table,
  Td,
  Th,
  Tr,
} from '../components/ui/primitives';
import {
  useAddSuppression,
  useCreateOAuthClient,
  useDeleteOAuthClient,
  useDeleteSuppression,
  useIsAdmin,
  useOAuthClientAction,
  useOAuthClients,
  useProjects,
  useSession,
  useSuppressions,
} from '../hooks/queries';
import { copyToClipboard, formatRelative } from '../lib/utils';
import { AccountCard } from './settings/AccountCard';
import { UsersCard } from './settings/UsersCard';

export const SettingsPage = () => {
  const { data: session, isLoading } = useSession();
  const isAdmin = useIsAdmin();
  const { data: clients } = useOAuthClients(isAdmin);
  const { data: projects } = useProjects();
  const createClient = useCreateOAuthClient();
  const clientAction = useOAuthClientAction();
  const deleteClient = useDeleteOAuthClient();
  const toast = useToast();

  const [createOpen, setCreateOpen] = useState(false);
  const [clientName, setClientName] = useState('Headless research agent');
  const [issued, setIssued] = useState<{ clientId: string; clientSecret: string } | null>(null);
  const [suppressionProject, setSuppressionProject] = useState('');

  const projectId = suppressionProject || projects?.[0]?.id || '';

  const copy = async (value: string, label: string) => {
    try {
      await copyToClipboard(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Could not copy — select the text manually');
    }
  };

  if (isLoading) return <LoadingBlock />;

  const mcpEndpoint = session?.config.mcpEndpoint ?? 'http://localhost:3311/mcp';

  return (
    <Page>
      <PageHeader
        title="Settings"
        description="How agents connect, what the server is configured to do, and the global do-not-contact list."
      />

      {session ? (
        <div className="mb-4 space-y-2">
          {session.warnings.mustChangePassword ? (
            <Warning>
              <strong>You are still using the password this account was created with.</strong> Pick
              your own under “Your account” below.
            </Warning>
          ) : null}
          {session.warnings.ephemeralJwtSecret ? (
            <Warning>
              <strong>No <Mono>APP_JWT_SECRET</Mono> is set,</strong> so a random one was generated.
              Every restart signs you out and invalidates MCP tokens. Generate one with{' '}
              <Mono>openssl rand -hex 32</Mono>.
            </Warning>
          ) : null}
          {session.warnings.emailDryRun ? (
            <div className="rounded-lg border border-line bg-surface px-4 py-3 text-[13px] text-muted">
              <ShieldCheck className="mr-1.5 inline h-4 w-4 text-positive" />
              Dry-run mode is on: nothing can reach a real inbox. Set{' '}
              <Mono>EMAIL_DRY_RUN=false</Mono> when you are ready to send.
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="mb-4 grid gap-4 lg:grid-cols-3">
        <AccountCard className="lg:col-span-2" />
        <Card>
          <CardHeader title="Session" description="How you are signed in" />
          <CardBody>
            <dl className="space-y-2 text-[13px]">
              {[
                ['Name', session?.user?.name ?? '—'],
                ['Email', session?.user?.email ?? session?.subject ?? '—'],
                ['Role', session?.role === 'ADMIN' ? 'Administrator' : 'Member'],
                ['Scopes', session?.scopes.join(', ') ?? '—'],
                ['Last sign-in', session?.user?.lastLoginAt ? formatRelative(session.user.lastLoginAt) : '—'],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between gap-3">
                  <dt className="shrink-0 text-faint">{label}</dt>
                  <dd className="min-w-0 truncate text-right text-ink" title={String(value)}>
                    {value}
                  </dd>
                </div>
              ))}
            </dl>
          </CardBody>
        </Card>
      </div>

      {isAdmin ? <UsersCard currentUserId={session?.user?.id ?? ''} /> : null}

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader
            title="MCP access"
            description="The MCP endpoint is protected by OAuth 2.1 — it is never open."
          />
          <CardBody className="space-y-4">
            <div>
              <span className="field-label">Endpoint</span>
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded-lg border border-line bg-raised px-3 py-2 font-mono text-[13px]">
                  {mcpEndpoint}
                </code>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Copy endpoint"
                  onClick={() => copy(mcpEndpoint, 'Endpoint')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div>
              <span className="field-label">Connect Claude Code (interactive)</span>
              <pre className="overflow-x-auto rounded-lg bg-raised px-3 py-2.5 font-mono text-[12px] leading-relaxed text-ink">
                claude mcp add --transport http rentx {mcpEndpoint}
              </pre>
              <p className="hint">
                Claude Code registers itself, opens the consent screen in your browser and asks for
                the admin password. Nothing else to configure.
              </p>
            </div>

            <div>
              <span className="field-label">Headless / stdio</span>
              <pre className="overflow-x-auto rounded-lg bg-raised px-3 py-2.5 font-mono text-[12px] leading-relaxed text-ink">
                {`RENTX_URL=${mcpEndpoint.replace(/\/mcp$/, '')} \\
RENTX_CLIENT_ID=… RENTX_CLIENT_SECRET=… \\
node packages/mcp/dist/index.js`}
              </pre>
              <p className="hint">
                For cron jobs and clients that only speak stdio. Create a machine client below to get
                a client id and secret — it uses the client-credentials grant, so no browser is
                needed.
              </p>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Server" description="Effective configuration" />
          <CardBody>
            <dl className="space-y-2 text-[13px]">
              {[
                ['Public URL', session?.config.publicUrl ?? '—'],
                ['Email sending', session?.config.emailDryRun ? 'dry run' : 'live'],
                [
                  'Allowlist',
                  session?.config.emailAllowlist.length
                    ? session.config.emailAllowlist.join(', ')
                    : 'not set (any recipient)',
                ],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between gap-3">
                  <dt className="shrink-0 text-faint">{label}</dt>
                  <dd className="min-w-0 truncate text-right text-ink" title={String(value)}>
                    {value}
                  </dd>
                </div>
              ))}
            </dl>
          </CardBody>
        </Card>
      </div>

      {isAdmin ? (
      <Card className="mt-4">
        <CardHeader
          title="OAuth clients"
          description="Every client that can reach the MCP endpoint. Disable one and its tokens stop working immediately."
          actions={
            <Button
              size="sm"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => setCreateOpen(true)}
            >
              New machine client
            </Button>
          }
        />
        {clients?.length ? (
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Client id</Th>
                <Th>Grants</Th>
                <Th>Origin</Th>
                <Th>Last used</Th>
                <Th className="w-32" />
              </tr>
            </thead>
            <tbody>
              {clients.map((client) => (
                <Tr key={client.id}>
                  <Td>
                    <span className="font-medium text-ink">{client.name}</span>
                    {client.disabled ? (
                      <Badge tone="danger" className="ml-2">
                        disabled
                      </Badge>
                    ) : null}
                  </Td>
                  <Td>
                    <button
                      type="button"
                      onClick={() => copy(client.clientId, 'Client id')}
                      className="font-mono text-xs text-muted hover:text-brand"
                      title="Click to copy"
                    >
                      {client.clientId}
                    </button>
                  </Td>
                  <Td className="text-xs text-muted">{client.grantTypes.join(', ')}</Td>
                  <Td>
                    <Badge tone={client.dynamic ? 'neutral' : 'brand'}>
                      {client.dynamic ? 'self-registered' : 'dashboard'}
                    </Badge>
                  </Td>
                  <Td className="whitespace-nowrap text-faint">
                    {client.lastUsedAt ? formatRelative(client.lastUsedAt) : 'never'}
                  </Td>
                  <Td>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          try {
                            await clientAction.mutateAsync({
                              id: client.id,
                              action: client.disabled ? 'enable' : 'disable',
                            });
                            toast.success(client.disabled ? 'Client enabled' : 'Client disabled');
                          } catch (error) {
                            toast.error(error);
                          }
                        }}
                      >
                        {client.disabled ? (
                          <Check className="h-4 w-4" />
                        ) : (
                          <Ban className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Delete client"
                        onClick={async () => {
                          try {
                            await deleteClient.mutateAsync(client.id);
                            toast.success('Client deleted');
                          } catch (error) {
                            toast.error(error);
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-danger" />
                      </Button>
                    </div>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <EmptyState
            icon={<KeyRound className="h-5 w-5" />}
            title="No clients yet"
            description="Connect Claude Code and it will register itself here. Create a machine client for headless runs."
          />
        )}
      </Card>
      ) : null}

      <Card className="mt-4">
        <CardHeader
          title="Do-not-contact list"
          description="Checked when leads are created and again before every send. Unsubscribes and bounces land here automatically."
          actions={
            projects && projects.length > 1 ? (
              <Select
                className="w-auto"
                value={projectId}
                onChange={(event) => setSuppressionProject(event.target.value)}
              >
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </Select>
            ) : null
          }
        />
        {projectId ? <SuppressionList projectId={projectId} /> : null}
      </Card>

      <Modal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setIssued(null);
        }}
        title="New machine client"
        description="Uses the client-credentials grant — for cron jobs and headless agents that cannot open a browser."
        footer={
          issued ? (
            <Button
              variant="primary"
              onClick={() => {
                setCreateOpen(false);
                setIssued(null);
              }}
            >
              Done
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                loading={createClient.isPending}
                onClick={async () => {
                  try {
                    const created = await createClient.mutateAsync({ name: clientName });
                    setIssued({ clientId: created.clientId, clientSecret: created.clientSecret });
                  } catch (error) {
                    toast.error(error);
                  }
                }}
              >
                Create client
              </Button>
            </>
          )
        }
      >
        {issued ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-[13px] text-warning">
              Copy the secret now. It is encrypted at rest and shown here once.
            </div>
            <Field label="Client id">
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded-lg border border-line bg-raised px-3 py-2 font-mono text-[13px]">
                  {issued.clientId}
                </code>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Copy client id"
                  onClick={() => copy(issued.clientId, 'Client id')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </Field>
            <Field label="Client secret">
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded-lg border border-line bg-raised px-3 py-2 font-mono text-[13px]">
                  {issued.clientSecret}
                </code>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Copy client secret"
                  onClick={() => copy(issued.clientSecret, 'Client secret')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </Field>
            <div>
              <span className="field-label">Ready-to-use command</span>
              <pre className="overflow-x-auto rounded-lg bg-raised px-3 py-2.5 font-mono text-[12px] leading-relaxed text-ink">
                {`RENTX_URL=${session?.config.publicUrl ?? ''} \\
RENTX_CLIENT_ID=${issued.clientId} \\
RENTX_CLIENT_SECRET=${issued.clientSecret} \\
node packages/mcp/dist/index.js`}
              </pre>
            </div>
          </div>
        ) : (
          <Field label="Name" htmlFor="client-name" hint="Shown in the client list and in audit events.">
            <Input
              id="client-name"
              autoFocus
              value={clientName}
              onChange={(event) => setClientName(event.target.value)}
            />
          </Field>
        )}
      </Modal>
    </Page>
  );
};

const Warning = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-[13px] leading-relaxed text-warning">
    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
    <div className="min-w-0">{children}</div>
  </div>
);

const SuppressionList = ({ projectId }: { projectId: string }) => {
  const { data: suppressions } = useSuppressions(projectId);
  const addSuppression = useAddSuppression(projectId);
  const deleteSuppression = useDeleteSuppression(projectId);
  const toast = useToast();
  const [form, setForm] = useState({ type: 'EMAIL', value: '', reason: '' });

  return (
    <>
      <div className="flex flex-wrap items-end gap-2 border-b border-line px-5 py-4">
        <Field label="Type" className="w-32">
          <Select
            value={form.type}
            onChange={(event) => setForm({ ...form, type: event.target.value })}
          >
            <option value="EMAIL">Email</option>
            <option value="DOMAIN">Domain</option>
          </Select>
        </Field>
        <Field label="Value" className="min-w-[16rem] flex-1">
          <Input
            value={form.value}
            placeholder={form.type === 'EMAIL' ? 'someone@example.com' : 'competitor.example'}
            onChange={(event) => setForm({ ...form, value: event.target.value })}
          />
        </Field>
        <Field label="Reason" className="min-w-[12rem] flex-1">
          <Input
            value={form.reason}
            placeholder="asked to stop"
            onChange={(event) => setForm({ ...form, reason: event.target.value })}
          />
        </Field>
        <Button
          variant="primary"
          loading={addSuppression.isPending}
          disabled={!form.value.trim()}
          onClick={async () => {
            try {
              await addSuppression.mutateAsync(form);
              toast.success('Added to the do-not-contact list');
              setForm({ ...form, value: '', reason: '' });
            } catch (error) {
              toast.error(error);
            }
          }}
        >
          Add
        </Button>
      </div>

      {suppressions?.length ? (
        <Table>
          <thead>
            <tr>
              <Th>Type</Th>
              <Th>Value</Th>
              <Th>Reason</Th>
              <Th>Added</Th>
              <Th className="w-16" />
            </tr>
          </thead>
          <tbody>
            {suppressions.map((entry) => (
              <Tr key={entry.id}>
                <Td>
                  <Badge tone={entry.type === 'DOMAIN' ? 'warning' : 'neutral'}>
                    {entry.type.toLowerCase()}
                  </Badge>
                </Td>
                <Td className="font-mono text-xs text-ink">{entry.value}</Td>
                <Td className="text-muted">{entry.reason ?? '—'}</Td>
                <Td className="whitespace-nowrap text-faint">{formatRelative(entry.createdAt)}</Td>
                <Td>
                  <div className="flex justify-end">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Remove"
                      onClick={async () => {
                        try {
                          await deleteSuppression.mutateAsync(entry.id);
                          toast.success('Removed');
                        } catch (error) {
                          toast.error(error);
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-danger" />
                    </Button>
                  </div>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <EmptyState
          title="Nothing suppressed"
          description="Unsubscribes and hard bounces are added here automatically."
        />
      )}
    </>
  );
};
